import { createServerFn } from '@tanstack/react-start';
import { createClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';
import type { Deal } from '@/lib/deals.functions';

export const HOME_OFFER_CATEGORIES = [
  { key: 'featured', name: 'Destaques', slug: null },
  { key: 'moda', name: 'Moda', slug: 'moda' },
  { key: 'calcados', name: 'Calçados', slug: 'calcados' },
  { key: 'casa', name: 'Casa', slug: 'casa' },
  { key: 'beleza', name: 'Beleza', slug: 'beleza-e-saude' },
  { key: 'eletrodomesticos', name: 'Eletrodomésticos', slug: 'eletrodomesticos' },
  { key: 'eletronicos', name: 'Eletrônicos', slug: 'eletronicos' },
] as const;

export type HomeOfferCategoryKey = (typeof HOME_OFFER_CATEGORIES)[number]['key'];
export type HomeOffersResult = Record<HomeOfferCategoryKey, Deal[]>;

type Candidate = Deal & { category_id: string | null };

const emptyResult = (): HomeOffersResult => ({
  featured: [],
  moda: [],
  calcados: [],
  casa: [],
  beleza: [],
  eletrodomesticos: [],
  eletronicos: [],
});

export const HOME_OFFERS_LIMIT = 16;
export const HOME_OFFERS_MIN_PER_TAB = 8;

/** Termos do título que confirmam (include) ou desmentem (exclude) a família da aba. */
const TITLE_RULES: Record<Exclude<HomeOfferCategoryKey, 'featured'>, { include: string[]; exclude: string[] }> = {
  moda: {
    include: ['camiseta', 'camisa', 'blusa', 'vestido', 'saia', 'calca', 'bermuda', 'short', 'jaqueta', 'casaco', 'moletom', 'blazer', 'regata', 'polo', 'macacao', 'cropped', 'top', 'body', 'legging', 'pijama', 'cueca', 'calcinha', 'sutia', 'biquini', 'maio', 'jeans', 'cardigan', 'sueter', 'colete', 'kimono', 'camisola', 'meia'],
    exclude: ['tenis', 'sapato', 'sandalia', 'bota', 'chinelo', 'perfume', 'celular', 'notebook', 'geladeira', 'panela', 'brinquedo', 'capa para', 'pelicula'],
  },
  calcados: {
    include: ['tenis', 'sapato', 'sapatenis', 'sandalia', 'bota', 'botina', 'chinelo', 'rasteira', 'scarpin', 'mocassim', 'sapatilha', 'tamanco', 'mule', 'slide', 'chuteira', 'papete', 'oxford', 'loafer', 'coturno', 'anabela', 'crocs'],
    exclude: ['camiseta', 'camisa', 'bolsa', 'mochila', 'perfume', 'meia', 'cadarco', 'palmilha', 'limpador', 'sapateira'],
  },
  casa: {
    include: ['jogo de cama', 'lencol', 'edredom', 'cobertor', 'travesseiro', 'toalha', 'cortina', 'tapete', 'almofada', 'panela', 'frigideira', 'talher', 'prato', 'copo', 'xicara', 'organizador', 'cadeira', 'mesa', 'sofa', 'poltrona', 'luminaria', 'abajur', 'espelho', 'quadro', 'vaso', 'colcha', 'manta', 'faqueiro', 'jarra', 'pote', 'cesto', 'prateleira', 'estante', 'rack', 'colchao', 'cama', 'guarda-roupa', 'armario', 'escrivaninha'],
    exclude: ['camiseta', 'vestido', 'tenis', 'sandalia', 'perfume', 'celular'],
  },
  beleza: {
    include: ['perfume', 'colonia', 'shampoo', 'condicionador', 'mascara capilar', 'creme', 'hidratante', 'serum', 'protetor solar', 'batom', 'base', 'corretivo', 'maquiagem', 'esmalte', 'desodorante', 'sabonete', 'oleo', 'locao', 'body splash', 'rimel', 'delineador', 'secador', 'chapinha', 'escova', 'barbeador', 'depilador', 'kit', 'gloss', 'po compacto', 'blush', 'suplemento', 'vitamina', 'whey'],
    exclude: ['camiseta', 'vestido', 'tenis', 'sandalia', 'bolsa', 'celular', 'panela'],
  },
  eletrodomesticos: {
    include: ['geladeira', 'refrigerador', 'fogao', 'cooktop', 'micro-ondas', 'microondas', 'lava', 'secadora', 'ar condicionado', 'ar-condicionado', 'ventilador', 'aspirador', 'liquidificador', 'batedeira', 'cafeteira', 'air fryer', 'fritadeira', 'forno', 'freezer', 'purificador', 'bebedouro', 'ferro de passar', 'sanduicheira', 'processador', 'mixer', 'espremedor', 'torradeira', 'chaleira', 'depurador', 'coifa', 'climatizador', 'aquecedor', 'adega', 'lava-loucas', 'panela eletrica', 'multiprocessador'],
    exclude: ['capa', 'filtro de reposicao', 'pelicula', 'camiseta', 'tenis'],
  },
  eletronicos: {
    include: ['smartphone', 'celular', 'iphone', 'galaxy', 'notebook', 'tablet', 'ipad', 'smart tv', 'televisor', 'monitor', 'fone', 'headset', 'caixa de som', 'soundbar', 'smartwatch', 'relogio inteligente', 'console', 'playstation', 'xbox', 'nintendo', 'camera', 'drone', 'teclado', 'mouse', 'impressora', 'roteador', 'ssd', 'hd externo', 'pendrive', 'carregador', 'echo', 'alexa', 'kindle', 'computador', 'pc gamer', 'projetor', 'tv '],
    exclude: ['capa', 'capinha', 'pelicula', 'suporte', 'camiseta', 'tenis', 'adesivo'],
  },
};

const ADULT_TERMS = ['vibrat', 'sex', 'erotic', 'lubrificante intimo', 'consolo', 'gel excitante'];
const FOOTWEAR = ['tenis', 'sapato', 'sandalia', 'bota', 'chinelo', 'tamanco', 'rasteira', 'sapatilha', 'chuteira'];
const JEWELRY = ['pingente', 'colar', 'anel', 'brinco', 'pulseira', 'bracelete', 'berloque'];
for (const key of ['moda', 'casa', 'beleza', 'eletrodomesticos', 'eletronicos'] as const) TITLE_RULES[key].exclude.push(...FOOTWEAR);
TITLE_RULES.moda.exclude.push(...JEWELRY, 'body splash', 'desodorante', 'colonia', 'hidratante');
TITLE_RULES.calcados.exclude.push(...JEWELRY);

const normalize = (text: string) => ` ${text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9-]+/g, ' ')} `;
const hasTerm = (title: string, term: string) => title.includes(` ${term}`);

/** O título precisa confirmar a família da aba; categoria cadastrada errada não basta. */
export function titleMatchesFamily(title: string, key: HomeOfferCategoryKey): boolean {
  const t = normalize(title);
  if (ADULT_TERMS.some((term) => hasTerm(t, term))) return false;
  if (key === 'featured') return true;
  const rules = TITLE_RULES[key];
  if (rules.exclude.some((term) => hasTerm(t, term))) return false;
  return rules.include.some((term) => hasTerm(t, term));
}

const validOffer = (deal: Candidate) =>
  !!deal.image_url && /^https:\/\//i.test(deal.image_url)
  && deal.original_price > deal.price && deal.discount_percent >= 5 && deal.discount_percent <= 90;

export function selectDiverseHomeOffers(candidates: Candidate[], categoryIds: Record<string, Set<string>>, limit = HOME_OFFERS_LIMIT): HomeOffersResult {
  const result = emptyResult();
  for (const category of HOME_OFFER_CATEGORIES) {
    const allowed = category.slug ? categoryIds[category.slug] : null;
    const eligible = candidates.filter((deal) =>
      validOffer(deal)
      && (!allowed || (!!deal.category_id && allowed.has(deal.category_id)))
      && titleMatchesFamily(deal.title, category.key));
    const picked: Candidate[] = [];
    const seenStores = new Set<string>();
    for (const deal of eligible) {
      if (picked.length === limit) break;
      if (seenStores.has(deal.brand_id)) continue;
      seenStores.add(deal.brand_id);
      picked.push(deal);
    }
    // Só repete loja quando não há outras lojas suficientes.
    for (const deal of eligible) {
      if (picked.length === limit) break;
      if (!picked.includes(deal)) picked.push(deal);
    }
    result[category.key] = picked;
  }
  return result;
}

export const getHomeOffers = createServerFn({ method: 'POST' }).handler(async (): Promise<HomeOffersResult> => {
  const key = process.env['SUPABASE_PUBLISHABLE_KEY'];
  const url = process.env['SUPABASE_URL'];
  if (!key || !url) return emptyResult();
  const db = createClient<Database>(url, key, {
    auth: { persistSession: false },
    global: { fetch: (input, init) => {
      const headers = new Headers(init?.headers);
      if (key.startsWith('sb_') && headers.get('Authorization') === `Bearer ${key}`) headers.delete('Authorization');
      headers.set('apikey', key);
      return fetch(input, { ...init, headers });
    } },
  }) as any;

  const freshSince = new Date(Date.now() - 10 * 86400_000).toISOString();
  const { data: categories } = await db.from('product_categories').select('id,slug,parent_id');
  const categoryRows = (categories ?? []) as { id: string; slug: string; parent_id: string | null }[];
  const categoryIds: Record<string, Set<string>> = {};
  for (const category of HOME_OFFER_CATEGORIES) {
    if (!category.slug) continue;
    const root = categoryRows.find((row) => !row.parent_id && row.slug === category.slug);
    categoryIds[category.slug] = new Set(root ? [root.id, ...categoryRows.filter((row) => row.parent_id === root.id).map((row) => row.id)] : []);
  }

  let orderedIds: string[] = [];
  try {
    const { multiSearchDeals, tsEsc } = await import('@/lib/typesense.server');
    const baseFilter = `available:=true && image_missing:=false && discount_percent:>=5 && discount_percent:<=90 && checked_at:>=${Math.floor(Date.parse(freshSince) / 1000)}`;
    const searches = HOME_OFFER_CATEGORIES.map((category) => {
      const ids = category.slug ? [...(categoryIds[category.slug] ?? [])] : [];
      const categoryFilter = ids.length ? ` && category_id:[${ids.map(tsEsc).join(',')}]` : '';
      return {
        q: '*', query_by: 'title', filter_by: `${baseFilter}${categoryFilter}`,
        sort_by: '_eval(image_missing:false):desc,discount_percent:desc,checked_at:desc',
        group_by: 'brand_id', group_limit: 2, per_page: 40, include_fields: 'id',
      };
    });
    const responses = await multiSearchDeals(searches, 1800);
    if (responses) {
      const idsByResponse = responses.flatMap((response: any) =>
        (response.grouped_hits ?? []).flatMap((group: any) => (group.hits ?? []).map((hit: any) => String(hit.document.id))),
      );
      orderedIds = [...new Set(idsByResponse)];
    }
  } catch {
    orderedIds = [];
  }

  const select = 'id,slug,brand_id,title,image_url,price,original_price,discount_percent,category,category_id,deeplink,checked_at,comparison_product_id,cmp:comparison_products(id,slug,store_count,review_status),brand:brands!inner(id,name,slug,logo_url,commission_type,donation_percentage,affiliate_network,is_active,network_paused,merged_into_id)';
  let rawRows: any[] = [];
  if (orderedIds.length) {
    const { data } = await db.from('deal_products').select(select).in('id', orderedIds);
    const byId = new Map(((data ?? []) as any[]).map((row) => [row.id, row]));
    rawRows = orderedIds.map((id) => byId.get(id)).filter(Boolean);
  } else {
    const { data } = await db.from('deal_products').select(select)
      .eq('available', true).eq('image_missing', false).is('parent_deal_id', null)
      .gte('checked_at', freshSince).gte('discount_percent', 5).lte('discount_percent', 90)
      .order('discount_percent', { ascending: false }).order('checked_at', { ascending: false }).limit(400);
    rawRows = (data ?? []) as any[];
  }

  const candidates: Candidate[] = rawRows
    .filter((row) => row.brand?.is_active && !row.brand?.network_paused && !row.brand?.merged_into_id)
    .filter((row) => !/amazon/i.test(row.brand?.name ?? '') && /^https:\/\//i.test(row.deeplink ?? ''))
    .filter((row) => Number(row.original_price) > Number(row.price))
    .map((row) => ({
      id: row.id, slug: row.slug, brand_id: row.brand_id, title: row.title, image_url: row.image_url,
      price: Number(row.price), original_price: Number(row.original_price), discount_percent: Number(row.discount_percent),
      category: row.category, category_id: row.category_id, deeplink: row.deeplink, checked_at: row.checked_at,
      brand: row.brand,
      compare: row.cmp && row.cmp.review_status === 'approved' && row.cmp.store_count >= 2
        ? { id: row.cmp.id, slug: row.cmp.slug, stores: row.cmp.store_count }
        : null,
    }));

  return selectDiverseHomeOffers(candidates, categoryIds);
});